/**
 * Where the stands are on the ground.
 *
 * The stand register says what a stand IS — its size, its bridge, its gate.
 * This file says WHERE it is: the position and heading of every stand drawn on
 * the aerodrome plan OEMA-323-14-01 Rev D, in the same UTM zone 37 metres the
 * rest of the survey uses, so the apron view lays the stands exactly over the
 * runways, taxiways and the scanned drawing.
 *
 * ── what each position rests on ────────────────────────────────────────
 *
 *   "drawing"  the stop mark (the small cross at the end of the lead-in line)
 *              read off the scanned sheet against a 50 m grid; good to about
 *              10 m, and to be replaced with the Annex H survey values.
 *   "label"    the position of the stand's label in the survey extract
 *              (data/airfield.json), which is within about 30 m.
 *
 * The heading is the direction the aircraft's nose points when parked: at the
 * main pier the stands face the pier (east on its west face, west on its east
 * face), on the north apron the two rows face each other's taxilane, and on
 * the west apron they face the old terminal.
 *
 * ── the drawing and the register are different lists ───────────────────
 *
 * The drawing has stands 103–135 at the main terminal and 1–29 on the west
 * apron. The register (the sample) has 101–121 plus remote, freight, GA and
 * maintenance stands under its own codes. A stand on the drawing that is not
 * in the register is shown and can be added; a stand in the register that is
 * not on the drawing (101, 102, R1–R4 …) is listed beside the map. Neither is
 * matched to the other by guessing.
 *
 * The pier and the terminal outlines are traced from the sheet for
 * orientation, and are marked as such.
 *
 * Pure. No React.
 */
import survey from "../data/airfield.json";

export type ApronArea = "pier-west" | "pier-east" | "north" | "west";

export interface StandSpot {
  /** the stand number as the drawing prints it */
  code: string;
  /** UTM 37 easting and northing of the stop mark, metres */
  E: number;
  N: number;
  /** unit vector the nose points along, in (east, north) */
  nose: [number, number];
  area: ApronArea;
  basis: "drawing" | "label";
  /** a MARS stand: one wide position, or two narrow ones marked L and R */
  mars?: boolean;
  /** served from the pier: a bridge reaches it */
  pier?: boolean;
}

export const AREA_LABEL: Record<ApronArea, string> = {
  "pier-west": "Main pier, west face",
  "pier-east": "Main pier, east face",
  north: "North apron",
  west: "West apron",
};

const unit = (e: number, n: number): [number, number] => {
  const l = Math.hypot(e, n);
  return [e / l, n / l];
};

/* the pier runs south-south-east to north-north-west; its faces are square to that */
const PIER_AXIS = unit(572088.5 - 572330.7, 2716363.7 - 2715014.3);
const FACE_EAST = unit(PIER_AXIS[1], -PIER_AXIS[0]);
const FACE_WEST: [number, number] = [-FACE_EAST[0], -FACE_EAST[1]];
const NORTH_AXIS = unit(571994 - 572052.5, 2716832.5 - 2716537.8);
const NORTH_EAST = unit(NORTH_AXIS[1], -NORTH_AXIS[0]);
const NORTH_WEST: [number, number] = [-NORTH_EAST[0], -NORTH_EAST[1]];

/* stop marks read off the sheet, 50 m grid, ±10 m */
const MAIN: [string, number, number, ApronArea, boolean?][] = [
  ["103", 572330.7, 2715014.3, "pier-west"],
  ["104", 572342.9, 2715085.0, "pier-west", true],
  ["105", 572326.4, 2715169.3, "pier-west", true],
  ["106", 572305.7, 2715253.6, "pier-west", true],
  ["107", 572289.3, 2715337.9, "pier-west", true],
  ["108", 572272.1, 2715422.9, "pier-west", true],
  ["109", 572259.3, 2715502.9, "pier-west"],
  ["110", 572245.0, 2715576.4, "pier-west"],
  ["111", 572230.0, 2715650.7, "pier-west"],
  ["112", 572214.3, 2715730.7, "pier-west"],
  ["113", 572197.1, 2715819.3, "pier-west"],
  ["114", 572180.0, 2715900.2, "pier-west"],
  ["115", 572165.0, 2715973.0, "pier-west"],
  ["116", 572151.5, 2716047.2, "pier-west"],
  ["117", 572135.8, 2716120.0, "pier-west"],
  ["118", 572121.5, 2716198.8, "pier-west"],
  ["119", 572105.0, 2716284.2, "pier-west"],
  ["120", 572088.5, 2716363.7, "pier-west"],
  ["121", 572194.3, 2716366.8, "pier-east", true],
  ["122", 572210.0, 2716282.0, "pier-east"],
  ["123", 572226.5, 2716204.0, "pier-east"],
  ["124", 572240.8, 2716129.8, "pier-east"],
  ["125", 572255.8, 2716057.0, "pier-east"],
  ["126", 572270.8, 2715983.5, "pier-east"],
  ["127", 572052.5, 2716537.8, "north"],
  ["128", 572038.3, 2716611.3, "north"],
  ["129", 572024.0, 2716684.8, "north"],
  ["130", 572009.0, 2716758.3, "north"],
  ["131", 571994.0, 2716832.5, "north"],
  ["132", 572155.3, 2716558.0, "north"],
  ["133", 572140.3, 2716632.3, "north"],
  ["134", 572126.0, 2716705.8, "north"],
  ["135", 572111.0, 2716779.3, "north"],
];

const NORTH_EAST_ROW = new Set(["132", "133", "134", "135"]);

/* the west apron: the survey extract's label positions (±30 m), facing the old terminal */
const WEST_FACING: Record<string, [number, number]> = {
  edge: unit(-0.985, -0.173),
  diagonal: unit(-0.87, -0.493),
  diagonalEast: unit(0.87, 0.493),
};
function westNose(code: string): [number, number] {
  const n = Number(code.replace(/\D/g, ""));
  if (n >= 25 && n <= 29) return WEST_FACING.diagonalEast;
  if (n >= 14 || /N|S/.test(code)) return WEST_FACING.diagonal;
  return WEST_FACING.edge;
}

interface SurveyAsset {
  id: string;
  name: string;
  cat: string;
  E: number;
  N: number;
}

function westStands(): StandSpot[] {
  const out: StandSpot[] = [];
  for (const a of (survey as { assets: SurveyAsset[] }).assets) {
    if (a.cat !== "STAND" || a.E > 571500) continue;
    const code = a.name.replace(/^STAND\s*/i, "").trim();
    if (!/^(\d{1,2}[NS]?|M)$/.test(code)) continue;
    out.push({ code, E: a.E, N: a.N, nose: westNose(code), area: "west", basis: "label" });
  }
  return out;
}

export const STAND_SPOTS: StandSpot[] = [
  ...MAIN.map(([code, E, N, area, mars]) => ({
    code,
    E,
    N,
    area,
    mars,
    basis: "drawing" as const,
    pier: area === "pier-west" || area === "pier-east",
    nose:
      area === "pier-west"
        ? FACE_EAST
        : area === "pier-east"
          ? FACE_WEST
          : NORTH_EAST_ROW.has(code)
            ? NORTH_WEST
            : NORTH_EAST,
  })),
  ...westStands(),
];

export function spotFor(code: string): StandSpot | undefined {
  return STAND_SPOTS.find((s) => s.code === code);
}

/* ── the ground the view is drawn in ──────────────────────────────────── */

/**
 * The view works in local metres: x east from the sheet's west edge, y SOUTH
 * from its north edge, so an SVG's y-down matches the drawing.
 */
export const EXTENT = (survey as { extent: { E_min: number; E_max: number; N_min: number; N_max: number } }).extent;
export const toX = (E: number) => E - EXTENT.E_min;
export const toY = (N: number) => EXTENT.N_max - N;
export const SHEET = { w: EXTENT.E_max - EXTENT.E_min, h: EXTENT.N_max - EXTENT.N_min };

/**
 * The view is turned so the main pier lies across the screen: the runways
 * along the top, the terminal along the bottom, north to the right. A wide
 * screen then shows the whole pier at a size where stands can be read.
 */
export const ROT = 100;
export const PIVOT = { x: SHEET.w / 2, y: SHEET.h / 2 };
const RAD = (ROT * Math.PI) / 180;
/** A local point as it lands on the turned view. */
export function turn(x: number, y: number): [number, number] {
  const dx = x - PIVOT.x;
  const dy = y - PIVOT.y;
  return [PIVOT.x + dx * Math.cos(RAD) - dy * Math.sin(RAD), PIVOT.y + dx * Math.sin(RAD) + dy * Math.cos(RAD)];
}

/** The SVG rotation that points the aircraft's nose along its heading. */
export function noseAngle(nose: [number, number]): number {
  return (Math.atan2(-nose[1], nose[0]) * 180) / Math.PI;
}

export interface Runway {
  name: string;
  a: [number, number];
  b: [number, number];
  width: number;
}

export function runways(): Runway[] {
  return (survey as { runways: { designators: string; end_a: number[]; end_b: number[]; width_m: number }[] }).runways.map(
    (r) => ({
      name: r.designators,
      a: [toX(r.end_a[0]), toY(r.end_a[1])],
      b: [toX(r.end_b[0]), toY(r.end_b[1])],
      width: r.width_m,
    }),
  );
}

/* ── buildings, traced from the sheet for orientation ─────────────────── */

const along = (p: [number, number], d: [number, number], m: number): [number, number] => [p[0] + d[0] * m, p[1] + d[1] * m];

/** A building outline in local metres, from four corners on the ground. */
function quad(corners: [number, number][]): string {
  return corners.map(([E, N]) => `${toX(E).toFixed(1)},${toY(N).toFixed(1)}`).join(" ");
}

/** The main pier: its west face 34 m beyond the west stops, 30 m deep. Traced. */
export function pierOutline(): string {
  const s: [number, number] = along([572330.7, 2714990], FACE_EAST, 30);
  const n: [number, number] = along([572088.5, 2716410], FACE_EAST, 50);
  return quad([s, n, along(n, FACE_EAST, 32), along(s, FACE_EAST, 32)]);
}

/** The main terminal, east of the pier at mid-length. Traced. */
export function terminalOutline(): string {
  const sw: [number, number] = along([572214.3, 2715600], FACE_EAST, 62);
  const nw: [number, number] = along([572180.0, 2715880], FACE_EAST, 62);
  return quad([sw, nw, along(nw, FACE_EAST, 215), along(sw, FACE_EAST, 215)]);
}

/** The point a bridge reaches on the aircraft side: just short of the nose. */
export function bridgeFoot(spot: StandSpot): [number, number] {
  return [toX(spot.E + spot.nose[0] * 4), toY(spot.N + spot.nose[1] * 4)];
}

/** The pier end of the bridge, where the gate is. */
export function gatePoint(spot: StandSpot): [number, number] {
  const d = spot.area === "pier-east" ? 28 : 30;
  return [toX(spot.E + spot.nose[0] * d), toY(spot.N + spot.nose[1] * d)];
}

/* ── the views the toolbar offers ─────────────────────────────────────── */

export interface ViewBox {
  x: number;
  y: number;
  w: number;
  h: number;
}

function boxAround(codes: (s: StandSpot) => boolean, pad: number): ViewBox {
  const pts = STAND_SPOTS.filter(codes).map((p) => turn(toX(p.E), toY(p.N)));
  const xs = pts.map((p) => p[0]);
  const ys = pts.map((p) => p[1]);
  const x = Math.min(...xs) - pad;
  const y = Math.min(...ys) - pad;
  return { x, y, w: Math.max(...xs) - x + pad, h: Math.max(...ys) - y + pad };
}

export const VIEWS: Record<"pier" | "north" | "west" | "stands" | "all", { label: string; box: () => ViewBox }> = {
  pier: { label: "Main pier", box: () => boxAround((s) => s.area === "pier-west" || s.area === "pier-east", 110) },
  north: { label: "North apron", box: () => boxAround((s) => s.area === "north", 150) },
  west: { label: "West apron", box: () => boxAround((s) => s.area === "west", 150) },
  stands: { label: "All stands", box: () => boxAround(() => true, 120) },
  all: {
    label: "Whole airfield",
    box: () => {
      const c = [turn(0, 0), turn(SHEET.w, 0), turn(0, SHEET.h), turn(SHEET.w, SHEET.h)];
      const xs = c.map((p) => p[0]);
      const ys = c.map((p) => p[1]);
      return { x: Math.min(...xs), y: Math.min(...ys), w: Math.max(...xs) - Math.min(...xs), h: Math.max(...ys) - Math.min(...ys) };
    },
  },
};

/* ── aircraft size on the ground ──────────────────────────────────────── */

/** Wingspan in metres at the top of each code letter, for drawing a parked aircraft. */
export const SPAN: Record<string, number> = { A: 15, B: 24, C: 36, D: 52, E: 65, F: 80 };

/* ── keeping the stands in view ───────────────────────────────────────── */

/** Every stand where it lands on the turned view. */
export const STAND_POINTS: { code: string; x: number; y: number }[] = STAND_SPOTS.map((s) => {
  const [x, y] = turn(toX(s.E), toY(s.N));
  return { code: s.code, x, y };
});

const finite = (b: ViewBox) => [b.x, b.y, b.w, b.h].every(Number.isFinite) && b.w > 0 && b.h > 0;

/**
 * A view moved back so a stand stays in it.
 *
 * Panning had no limit: a few long drags (Safari, 2026-10-10) carried the map
 * thousands of metres past the airfield and the screen read as blank. Now, if
 * no stand lies in the inner 80% of the view, the view slides — by no more
 * than it must — until the stand nearest its centre does. The stands form an
 * L, not a rectangle, so a bounding-box limit still let a corner go empty;
 * this one cannot. A view with no usable numbers is refused.
 */
export function keepOnStands(b: ViewBox, previous?: ViewBox): ViewBox {
  if (!finite(b)) return previous && finite(previous) ? previous : VIEWS.stands.box();
  const inner = { x: b.x + b.w * 0.1, y: b.y + b.h * 0.1, w: b.w * 0.8, h: b.h * 0.8 };
  if (standsInView(inner) > 0) return b;
  const s = nearestStand(b.x + b.w / 2, b.y + b.h / 2);
  let dx = 0;
  let dy = 0;
  if (s.x < inner.x) dx = s.x - inner.x;
  else if (s.x > inner.x + inner.w) dx = s.x - (inner.x + inner.w);
  if (s.y < inner.y) dy = s.y - inner.y;
  else if (s.y > inner.y + inner.h) dy = s.y - (inner.y + inner.h);
  return { x: b.x + dx, y: b.y + dy, w: b.w, h: b.h };
}

/** How many stands fall inside a view. */
export function standsInView(b: ViewBox): number {
  return STAND_POINTS.filter((p) => p.x >= b.x && p.x <= b.x + b.w && p.y >= b.y && p.y <= b.y + b.h).length;
}

/** The stand nearest a point on the view. */
export function nearestStand(x: number, y: number): { code: string; x: number; y: number } {
  let best = STAND_POINTS[0];
  let d = Infinity;
  for (const p of STAND_POINTS) {
    const q = (p.x - x) ** 2 + (p.y - y) ** 2;
    if (q < d) {
      d = q;
      best = p;
    }
  }
  return best;
}
